// Fat (thick) line helpers for segment boundaries.
//
// Plain THREE.LineBasicMaterial ignores `linewidth` on Windows/Chrome (WebGL
// clamps lines to 1px). To draw a genuinely thicker segment outline we use the
// Line2 family (LineSegments2 + LineSegmentsGeometry + LineMaterial), which
// renders width in screen pixels.
//
// LineMaterial needs its `resolution` kept in sync with the renderer size, so
// updateLineResolutions() is called from SceneManager.handleResize().

import * as THREE from "three";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

// Boundary thickness, in screen pixels. Bump this to make the outline thicker.
export const SEGMENT_LINE_WIDTH = 2.5;

// Last known renderer resolution; used when a wireframe is created before the
// next resize. Kept current by updateLineResolutions().
const lastResolution = new THREE.Vector2(
  typeof window !== "undefined" ? window.innerWidth : 1,
  typeof window !== "undefined" ? window.innerHeight : 1
);

function flattenPoints(points) {
  const out = [];
  for (const p of points) {
    out.push(p.x, p.y, p.z);
  }
  return out;
}

/**
 * Build a thick segment-boundary line from edge points (pairs: p1,p2, p2,p3, …).
 * Mirrors the old createEdgesWireframe() output (name "wireframe", renderOrder 3)
 * so the rest of the code can keep treating it as the segment's wireframe child.
 */
export function createSegmentWireframe(edgePoints, colorHex) {
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(flattenPoints(edgePoints));

  const material = new LineMaterial({
    color: colorHex,
    linewidth: SEGMENT_LINE_WIDTH,
    worldUnits: false, // width in screen pixels
    transparent: true,
    depthTest: false,
  });
  material.resolution.copy(lastResolution);

  const line = new LineSegments2(geometry, material);
  line.name = "wireframe";
  line.renderOrder = 3;
  line.frustumCulled = false;
  return line;
}

/**
 * Update an existing segment wireframe's geometry to new edge points.
 * Works for both the fat LineSegments2 and (defensively) a plain LineSegments.
 */
export function updateSegmentWireframe(wireframe, edgePoints) {
  if (!wireframe?.geometry) return;

  const geometry = wireframe.geometry;
  if (typeof geometry.setPositions === "function") {
    geometry.setPositions(flattenPoints(edgePoints));
    geometry.attributes?.instanceStart && (geometry.attributes.instanceStart.needsUpdate = true);
    if (typeof geometry.computeBoundingSphere === "function") {
      geometry.computeBoundingSphere();
    }
    return;
  }

  // Fallback: legacy thin LineSegments (BufferGeometry position attribute).
  const newGeometry = new THREE.BufferGeometry().setFromPoints(edgePoints);
  if (newGeometry.attributes.position) {
    geometry.setAttribute("position", newGeometry.attributes.position);
    geometry.attributes.position.needsUpdate = true;
  }
  newGeometry.dispose();
}

/**
 * Keep every fat LineMaterial's resolution matched to the renderer size.
 * Called from SceneManager.handleResize(). No registry needed — we just walk
 * the scene, so deleted segments can't leak stale materials.
 */
export function updateLineResolutions(scene, width, height) {
  if (width > 0 && height > 0) {
    lastResolution.set(width, height);
  }
  if (!scene) return;
  scene.traverse((obj) => {
    const material = obj.material;
    if (material && material.isLineMaterial && material.resolution) {
      material.resolution.set(lastResolution.x, lastResolution.y);
    }
  });
}
